import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import OnboardingProgressBadge from '../../components/onboarding/OnboardingProgressBadge';

const AssetsListPage: React.FC = () => {
  useDocumentTitle('Assets');
  const { assets } = useAuth();
  const navigate = useNavigate();

  const handleAddAsset = () => {
    navigate('/setup/asset');
  };

  const handleViewAsset = (assetId: number) => {
    navigate(`/assets/${assetId}`);
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="h3 mb-1">My Assets</h2>
                <p className="text-muted mb-0">Manage your digital assets and monitoring integrations</p>
              </div>
              <button className="btn btn-primary" onClick={handleAddAsset}>
                <i className="bi bi-plus-circle me-2"></i>Add New Asset
              </button>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {assets.map((asset) => (
            <div key={asset.id} className="col-12 col-md-6 col-xl-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <div className="d-flex align-items-start mb-3">
                    <div className="p-3 bg-primary bg-opacity-10 text-primary rounded me-3">
                      <i className={`bi ${
                        asset.platform_website ? 'bi-globe' :
                        asset.platform_mobile_app ? 'bi-phone' :
                        asset.platform_desktop_app ? 'bi-laptop' :
                        'bi-box-seam'
                      } fs-3`}></i>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="card-title mb-1">{asset.name}</h5>
                      <p className="text-muted small mb-2">
                        <code className="bg-light px-2 py-1 rounded">{asset.tracking_id}</code>
                      </p>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      {/* Status Badge */}
                      {asset.is_verified ? (
                        <span className="badge bg-success d-flex align-items-center gap-1">
                          <i className="bi bi-check-circle-fill"></i>
                          Live
                        </span>
                      ) : (
                        <span className="badge bg-warning text-dark d-flex align-items-center gap-1">
                          <i className="bi bi-exclamation-triangle-fill"></i>
                          Not Integrated
                        </span>
                      )}

                      <OnboardingProgressBadge assetId={asset.id} />

                      {/* Active/Inactive Status */}
                      {!asset.is_active && (
                        <span className="badge bg-secondary">Inactive</span>
                      )}

                      {/* Platform Badges */}
                      {asset.platform_website && (
                        <span className="badge bg-primary bg-opacity-75">Website</span>
                      )}
                      {asset.platform_mobile_app && (
                        <span className="badge bg-info">Mobile</span>
                      )}
                      {asset.platform_desktop_app && (
                        <span className="badge bg-dark">Desktop</span>
                      )}
                      {asset.platform_iot && (
                        <span className="badge bg-secondary">IoT</span>
                      )}
                      {asset.platform_endpoints && (
                        <span className="badge bg-success">Endpoint Devices</span>
                      )}
                    </div>
                  </div>

                  <div className="d-grid">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleViewAsset(asset.id)}
                    >
                      <i className="bi bi-eye me-2"></i>View
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {assets.length === 0 && (
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="bi bi-box-seam fs-1 text-muted d-block mb-3"></i>
                  <h5>No Assets Yet</h5>
                  <p className="text-muted mb-4">Add your first digital asset to start monitoring</p>
                  <button className="btn btn-primary" onClick={handleAddAsset}>
                    <i className="bi bi-plus-circle me-2"></i>Add Your First Asset
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AssetsListPage;
